import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Form, json, Outlet, redirect, useMatches, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './create.css'

// COMPONENTS
import * as Icons from '@icons'
import CharacterCount from '@components/atoms/character-count'
import CurrencyTextField from '@components/molecules/currency-textfield'
import { Column } from '@components/templates/column'
import TextField from '@components/atoms/textfield'
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'
import GroupCell from '@components/molecules/group-cell'
import RestrictedPurchases from '@components/organisims/restricted-purchases'
import GroupCardCreate from '@components/organisims/group-card-create'
import StepTracker from '@components/organisims/step-tracker'
import GroupBannerCreate from '@components/organisims/group-banner-create'
import Switch from '@components/atoms/switch'

// ROUTES
import { PMRoute } from './pm'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createLimitedAction } from '@utils/actions'
import { createResponseObj, VirtualCardAPI } from '@apis'
import { useGroupSettings } from '../_localStore/_hooks'
import { useInput, useCurrencyInput, useCheckbox } from '@hooks/inputs'
import { flatten } from '@utils/string-helper'
import { groupMax, min, links } from '@constants'
import { isNonEmptyString } from '@utils/type-checkers'
import { toPennies } from '@utils/number-helper'
import { parsePledge, parseTeraUserData, parseUserLimits } from '@schemas'
import { createKycVerifiedLoader, refreshClerkUser } from '@/utils/loaders'
import { createQueryParameters } from '@/utils/object-helper'
import { queryClient } from '@/lib/queryClient'


interface Props { }

const Layout: FC<Props> = () => {
    const currentStep = useCurrentStep('/group', ['/', '/pm'])
    const { state } = useGroupSettings()
    const { user } = useUser()

    const isMerchant = !!user?.publicMetadata.merchant?.id

    // Determine lastFour based on selected card type
    const physicalCard = user?.publicMetadata.tera?.physicalCard
    const virtualCard = user?.publicMetadata.tera?.virtualCard
    const linkedCard = state.requestBody.linkPhysicalCard
        ? physicalCard
        : state.requestBody.linkVirtualCard
            ? virtualCard
            : undefined
    const lastFour = linkedCard?.lastFour

    return (<>
        {!isMerchant && <Column.Content className='group-banner-section' style={{ padding: '0rem' }}>
            <StepTracker currentStep={currentStep} steps={3} />
        </Column.Content>}
        <Column.Content className="group-create-screen">
            {currentStep === 1 && <GroupBannerCreate currentStep={currentStep} />}
            {currentStep !== 2 && <GroupCardCreate groupState={state} lastFour={lastFour} />}
            <Outlet />
        </Column.Content>
    </>)
}

/**
 * @description Temporary utility hook for the Step Tracker. It uses the Matches api to test the current route against all route paths.
 */
const useCurrentStep = (root: string, paths: string[]) => {
    const [...matches] = useMatches()
    const count = matches.findIndex((path) => path.pathname === root) + 1
    matches.splice(0, count) // Finds and removes the `root` pathname and everything before it.
    let currentStep = paths.length

    for (const path of [...paths].reverse()) {
        if (matches.find(({ pathname }) => pathname.includes(path))) break;
        else currentStep -= 1;
    }

    return currentStep
}

// ==================== Goal Details Component (Index Route) ====================

const formId = "group-create-setup-title"
const titleMax = 25

const GoalDetailsComponent: FC<{}> = () => {
    const { user, isLoaded } = useUser()
    const navigate = useNavigate()

    const isMerchant = !!user?.publicMetadata.merchant?.id

    const { updateRequestBody, state } = useGroupSettings()
    const { requestBody } = state
    const { title: storedTitle, targetAmount: storedTargetAmount } = requestBody

    const physicalCard = user?.publicMetadata.tera?.physicalCard
    const linkPhysicalCard = state.requestBody.linkPhysicalCard
    const virtualCard = user?.publicMetadata.tera?.virtualCard
    const linkVirtualCard = state.requestBody.linkVirtualCard ?? (!!virtualCard && !linkPhysicalCard && !virtualCard?.activeGroup)

    const [isMerchantGroup, setIsMerchantGroup] = useState(false)

    const [invalid, setInvalid] = useState(false)

    const [title, titleProps] = useInput({ initValue: storedTitle, onBlur: () => updateRequestBody({ title }) })
    const [targetAmount, targetAmountProps] = useCurrencyInput({
        initValue: `${(storedTargetAmount || 0) / 100}`,
        onFocus: () => { if (!!invalid) setInvalid(false) },
        max: groupMax,
        onBlur: (amount) => updateRequestBody({ targetAmount: toPennies(amount) })
    })
    const [merchantGroupOrganizer, merchantGroupOrganizerProps] = useInput({ initValue: '' })
    const submittable = !!title && title.length <= titleMax
        && (!targetAmount || (toPennies(targetAmount) >= min && toPennies(targetAmount) <= groupMax))
        && (!isMerchantGroup || !!merchantGroupOrganizer)

    const [activeGroup, setActiveGroup] = useState<Group>()
    const [gettingActiveGroup, setGettingActiveGroup] = useState(false)
    const [closingActiveGroup, setClosingActiveGroup] = useState(false)

    const [_physicalCard, , setPhysicalCard] = useCheckbox({
        initValue: linkPhysicalCard,
        onChange(isChecked) {
            if (!!physicalCard?.activeGroup && !!isChecked) getActiveGroup(physicalCard.activeGroup)

            updateRequestBody({ linkPhysicalCard: isChecked })
            if (linkVirtualCard) setVirtualCard(false)
            return isChecked
        }
    })

    const [_virtualCard, , setVirtualCard] = useCheckbox({
        initValue: linkVirtualCard,
        onChange(isChecked) {
            if (!!virtualCard?.activeGroup && !!isChecked) getActiveGroup(virtualCard.activeGroup)

            updateRequestBody({ linkVirtualCard: isChecked })
            if (linkPhysicalCard) setPhysicalCard(false)
            return isChecked
        }
    })

    useEffect(() => {
        if (!!virtualCard && state.requestBody.linkVirtualCard === undefined && !linkPhysicalCard && !virtualCard?.activeGroup) updateRequestBody({ linkVirtualCard: true })
    }, [virtualCard, state.requestBody.linkVirtualCard, linkPhysicalCard])

    // Transaction information for the active group if it defined
    const activeGroupTransactions = useMemo(() => {
        if (!!activeGroup?.transactions) {
            const items = activeGroup.transactions
            if (!items) return []

            return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
        }

        return []
    }, [activeGroup?.transactions])
    const activeGroupHasTransactions = useMemo(() => !!activeGroupTransactions && activeGroupTransactions?.length > 0, [activeGroupTransactions])

    const activeGroupRef = useRef<HTMLDialogElement>(null)
    function openActiveGroupWindow() { if (activeGroupRef.current) activeGroupRef.current.showModal() }
    function closeActiveGroupWindow(resetCheckbox?: boolean) {
        if (activeGroupRef.current) {
            if (linkPhysicalCard && resetCheckbox) setPhysicalCard(false)
            if (linkVirtualCard && resetCheckbox) setVirtualCard(false)

            activeGroupRef.current.close()
            setClosingActiveGroup(false)
        }
    }

    async function getActiveGroup(_activeGroupId: string) {
        console.log(_activeGroupId)
        openActiveGroupWindow()
        setGettingActiveGroup(true)

        if (!isNonEmptyString(_activeGroupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(_activeGroupId)}`
        })

        console.log("Get Group Data:", { _activeGroupId })
        const _activeGroup = await VirtualCardAPI.GetGroupRequest.fetch(_activeGroupId)
        setActiveGroup(_activeGroup)
        setGettingActiveGroup(false)
        return json('success', { status: 200 })
    }

    async function closeActiveGroup() {
        setClosingActiveGroup(true)

        if (!isNonEmptyString(activeGroup?.id)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(activeGroup?.id)}`
        })

        console.log("Close Group Data:", { activeGroupId: activeGroup?.id })

        if (!activeGroupHasTransactions) await VirtualCardAPI.CancelGroupRequest.fetch(activeGroup?.id)
        else await VirtualCardAPI.CompleteGroupRequest.fetch(activeGroup?.id)

        queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        if (user) user.publicMetadata = {
            tera: parseTeraUserData({ ...user.publicMetadata.tera, activeGroup: undefined })
        }
        setClosingActiveGroup(false)
        closeActiveGroupWindow(false)
        return json('success', { status: 200 })
    }

    function handleBack() {
        navigate('/account')
    }

    function handleMerchantToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setIsMerchantGroup(e.target.checked)
        if (linkPhysicalCard) setPhysicalCard(false)
        if (linkVirtualCard) setVirtualCard(false)
    }

    function focusFormInput(form: HTMLFormElement | null, fieldName: string) {
        const field = form?.querySelector<HTMLElement>(`[name="${fieldName}"]:not([type="hidden"])`)
        field?.focus()
    }

    function handleTitleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key !== 'Enter') return

        e.preventDefault()
        e.stopPropagation()
        focusFormInput(e.currentTarget.form, isMerchantGroup ? 'merchantGroupOrganizer' : 'targetAmount')
    }

    function handleMerchantOrganizerKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key !== 'Enter') return

        e.preventDefault()
        e.stopPropagation()
        focusFormInput(e.currentTarget.form, 'targetAmount')
    }

    if (!isLoaded) return null
    else return (<>
        <Column.Header.Start>
            <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" className="w-[1.4rem] h-[1.4rem]" draggable="false" />
            </a>
        </Column.Header.Start>
        <Form id={formId} className={flatten("flex flex-col items-stretch", (!!physicalCard || !!virtualCard) ? "physical-card" : "")} style={{ height: '90%' }} method="POST">
            <input type="hidden" name="isMerchantGroup" value={`${isMerchantGroup}`} />
            <input type="hidden" name="merchantGroupOrganizer" value={`${merchantGroupOrganizer}`} />
            <input type="hidden" name="requestBody" value={JSON.stringify(requestBody)} />
            <div className='flex flex-col gap-4'>
                {isMerchant && <div className="merchant-toggle">
                    <div className="merchant-toggle-content">
                        <div className="bubble-sml flex items-center justify-center !bg-green-200 shrink-0">
                            <Icons.Store tint="var(--splitcard-green-600)" width="1.5rem" height="1.5rem" />
                        </div>
                        <div className="merchant-toggle-info">
                            <p className="merchant-toggle-title">Create Merchant Group</p>
                            <p className="merchant-toggle-description" style={{ textWrap: 'wrap'}}>Toggle to create a group for your customers.</p>
                        </div>
                    </div>
                    <div className="merchant-toggle-toggle">
                        <Switch
                            checked={isMerchantGroup}
                            onChange={handleMerchantToggleChange}
                            className="merchant-toggle-switch"
                        />
                    </div>
                </div>}
                {((!!physicalCard || !!virtualCard) && !isMerchantGroup) && (
                    <div className="card-type-selector" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '0.5rem' }}>
                        {/* Reloadable Card Option */}
                        {!!virtualCard && (
                            <div
                                onClick={() => setVirtualCard(!linkVirtualCard)}
                                className="card-type-option"
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '1rem 0.5rem',
                                    borderRadius: 'var(--border-radius-md)',
                                    border: linkVirtualCard ? '2px solid var(--splitcard-blue-500)' : '2px solid var(--splitcard-gray-200)',
                                    background: linkVirtualCard ? 'var(--splitcard-blue-100)' : 'var(--splitcard-white)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {linkVirtualCard && (
                                    <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', width: '1rem', height: '1rem', borderRadius: '50%', background: 'var(--splitcard-blue-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M8.5 2.5L4 7.5L1.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </div>
                                )}
                                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', background: linkVirtualCard ? 'var(--splitcard-blue-500)' : 'var(--splitcard-gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17 2l4 4-4 4" stroke={linkVirtualCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 11V9a4 4 0 014-4h14" stroke={linkVirtualCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M7 22l-4-4 4-4" stroke={linkVirtualCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M21 13v2a4 4 0 01-4 4H3" stroke={linkVirtualCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </div>
                                <strong style={{ fontSize: '0.875rem', color: linkVirtualCard ? 'var(--splitcard-blue-500)' : undefined }}>Reloadable</strong>
                                <span className="text-body-2 text-gray-500" style={{ fontSize: '0.75rem' }}>Last four: {virtualCard.lastFour}</span>
                            </div>
                        )}

                        {/* Single Use Option */}
                        <div
                            onClick={() => {
                                if (linkPhysicalCard) setPhysicalCard(false)
                                if (linkVirtualCard) setVirtualCard(false)
                            }}
                            className="card-type-option"
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '1rem 0.5rem',
                                borderRadius: 'var(--border-radius-md)',
                                border: (!linkPhysicalCard && !linkVirtualCard) ? '2px solid var(--splitcard-blue-500)' : '2px solid var(--splitcard-gray-200)',
                                background: (!linkPhysicalCard && !linkVirtualCard) ? 'var(--splitcard-blue-100)' : 'var(--splitcard-white)',
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {(!linkPhysicalCard && !linkVirtualCard) && (
                                <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', width: '1rem', height: '1rem', borderRadius: '50%', background: 'var(--splitcard-blue-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M8.5 2.5L4 7.5L1.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </div>
                            )}
                            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', background: (!linkPhysicalCard && !linkVirtualCard) ? 'var(--splitcard-blue-500)' : 'var(--splitcard-gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="14" height="19" viewBox="0 0 14 19" fill="none"><path d="M10.8359 0.833252H2.5026C1.58213 0.833252 0.835938 1.57944 0.835938 2.49992V15.8333C0.835938 16.7537 1.58213 17.4999 2.5026 17.4999H10.8359C11.7564 17.4999 12.5026 16.7537 12.5026 15.8333V2.49992C12.5026 1.57944 11.7564 0.833252 10.8359 0.833252Z" stroke={(!linkPhysicalCard && !linkVirtualCard) ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                            <strong style={{ fontSize: '0.875rem', color: (!linkPhysicalCard && !linkVirtualCard) ? 'var(--splitcard-blue-500)' : undefined }}>Single Use</strong>
                            <span className="text-body-2 text-gray-500" style={{ fontSize: '0.75rem' }}>New virtual card</span>
                        </div>

                        {/* Physical Card Option */}
                        {!!physicalCard && (
                            <div
                                onClick={() => setPhysicalCard(!linkPhysicalCard)}
                                className="card-type-option"
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '1rem 0.5rem',
                                    borderRadius: 'var(--border-radius-md)',
                                    border: linkPhysicalCard ? '2px solid var(--splitcard-blue-500)' : '2px solid var(--splitcard-gray-200)',
                                    background: linkPhysicalCard ? 'var(--splitcard-blue-100)' : 'var(--splitcard-white)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {linkPhysicalCard && (
                                    <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', width: '1rem', height: '1rem', borderRadius: '50%', background: 'var(--splitcard-blue-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M8.5 2.5L4 7.5L1.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </div>
                                )}
                                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', background: linkPhysicalCard ? 'var(--splitcard-blue-500)' : 'var(--splitcard-gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke={linkPhysicalCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" /><path d="M2 10h20" stroke={linkPhysicalCard ? 'white' : 'var(--splitcard-gray-500)'} strokeWidth="2" /></svg>
                                </div>
                                <strong style={{ fontSize: '0.875rem', color: linkPhysicalCard ? 'var(--splitcard-blue-500)' : undefined }}>Physical Card</strong>
                                <span className="text-body-2 text-gray-500" style={{ fontSize: '0.75rem' }}>Last four: {physicalCard.lastFour}</span>
                            </div>
                        )}
                    </div>
                )}
                <h2 style={{ textWrap: 'unset' }}>{isMerchantGroup ? 'Group Name' : 'Name Your Group'}</h2>
                <TextField
                    {...titleProps}
                    name="title"
                    enterKeyHint="next"
                    onKeyDown={handleTitleKeyDown}
                    placeholder="Cowboys Game, Concert Tickets, etc..."
                    inlineElementRight={<CharacterCount label={title} max={titleMax} />}
                    minLength={1}
                    maxLength={titleMax}
                    required
                />
                {isMerchantGroup && (<>
                    <h2 className='mt-4'>Add Organizer</h2>
                    <TextField
                        {...merchantGroupOrganizerProps}
                        name="merchantGroupOrganizer"
                        enterKeyHint="next"
                        onKeyDown={handleMerchantOrganizerKeyDown}
                        placeholder="John Doe"
                        inlineElementRight={<CharacterCount label={merchantGroupOrganizer} max={titleMax} />}
                        minLength={1}
                        maxLength={titleMax}
                        required
                    />
                </>)}
                <h2 className='mt-4'>Enter Goal Amount</h2>
                <CurrencyTextField
                    centered={true}
                    invalid={invalid}
                    name="targetAmount"
                    {...targetAmountProps}
                />
                <p className='text-body-2 text-gray-500'>The goal amount is optional & you can always adjust the goal later.</p>
            </div>
            <div className='flex flex-col gap-4'>
                <RestrictedPurchases />
                <p className='text-body text-gray-500 bg-gray-50 border border-gray-100 rounded-[var(--border-radius)] p-4 text-center'>
                    By proceeding you are agreeing to our <a onClick={() => window.open(links.termsOfUse, '_blank')}>terms & conditions</a>
                </p>
            </div>
            <Column.Footer>
                <Button form={formId} type="submit" disabled={!submittable}>{isMerchantGroup ? 'Create Group' : 'Next'}</Button>
            </Column.Footer>
        </Form>
        <dialog ref={activeGroupRef}>
            <div>
                <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                    <h2 style={{ textAlign: 'center' }}>This Group is Linked to Your Card</h2>
                    <p style={{ textAlign: 'center' }}>You can only have one group linked to your {activeGroup?.card?.cardType === 'physical' ? 'physical' : 'reloadable'} card at a time, you can finalize or tap keep activated to use a one time virtual card.</p>
                    {(gettingActiveGroup || !activeGroup)
                        ? (<div className='flex flex-row items-center justify-center gap-2 py-12'>
                            <Spinner indicator='circle' />
                        </div>)
                        : (<span style={{ pointerEvents: closingActiveGroup ? 'none' : undefined }} onClick={() => closeActiveGroupWindow(false)}>
                            <GroupCell group={activeGroup}></GroupCell>
                        </span>)
                    }
                    <div className="flex flex-row gap-2.5">
                        <Button className='flex-1' theme="light" disabled={closingActiveGroup} onClick={() => closeActiveGroupWindow(true)}>Keep Activated</Button>
                        <Button className='flex-1' theme="primary" disabled={closingActiveGroup} onClick={closeActiveGroup}>
                            {!activeGroupHasTransactions
                                ?
                                (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Cancel Group')
                                :
                                (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Close Group')
                            }
                        </Button>
                    </div>
                </Form>
            </div>
        </dialog>
    </>)
}

const goalDetailsAction = createLimitedAction({
    limit: 'createGroups',
    getRedirectPath: () => '/account',
}, async ({ request }) => {
    const formData = await request.formData()
    const title = formData.get('title')
    const isMerchantGroup = formData.get('isMerchantGroup') === 'true'
    const merchantGroupOrganizer = formData.get('merchantGroupOrganizer') as string

    if (isMerchantGroup) {
        if (!isNonEmptyString(title) || title.length > titleMax) {
            throw new Error(`Group name must be a min of 1 and max of ${titleMax} characters.`);
        }

        const requestBody = JSON.parse(formData.get('requestBody') as string)
        const group: CreateGroupRequestBody = { ...requestBody, isMerchant: true, pledges: [parsePledge({
                name: merchantGroupOrganizer
            }), ...(requestBody.pledges || [])] }
        const groupId = await VirtualCardAPI.CreateGroupRequest.fetch(group)

        queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        sessionStorage.setItem('showNotificationPrompt', 'true')

        return redirect(isMerchantGroup ? `/group/${groupId}?fromMerchantGroupCreate=true` : `/group/${groupId}/invite`)
    }

    if (!isNonEmptyString(title) || title.length > titleMax) throw new Error(`Group name must be a min of 1 and max of ${titleMax} characters.`);
    else {
        const from = new URL(request.url).searchParams.get('from')
        const to = (isNonEmptyString(from) && (from.at(0) === '/' || from.at(0) === '.')) ? from : './pm'
        return redirect(to)
    }
})

// ==================== Loaders & Route Config ====================

const loader = createKycVerifiedLoader(async (args, isKycVerified, isNewUser, Clerk) => {
    if (!isKycVerified && isNewUser) {
        const queryParams = createQueryParameters({ from: new URL(args.request.url).pathname })
        return redirect(`/sign-up/kyc${queryParams}`)
    }

    await refreshClerkUser(Clerk)
    const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)
    if (!userLimits.createGroups) return redirect('/account')
    else return json(null, 200)
})

const GoalDetailsRoute: RouteObject = {
    index: true,
    Component: GoalDetailsComponent,
    action: goalDetailsAction
}

const CreateGroupRoute: RouteObject = {
    element: <Layout />,
    shouldRevalidate: () => false,
    children: [
        GoalDetailsRoute,
        PMRoute,
    ],
    loader
}

export default CreateGroupRoute
